<script>
import ListHeader from '@/components/common/ListHeader.vue';
import FilterChips from '@/components/common/FilterChips.vue';
import QuestionFilters from '@/components/questions/QuestionFilters.vue';
import QuestionRow from '@/components/questions/QuestionRow.vue';
import QuestionPanel from '@/components/questions/QuestionPanel.vue';
import NativeFieldsCard from '@/components/questions/NativeFieldsCard.vue';
import { questionTypeLabel } from '@/constants/question_types';
import { questionService } from '@/shared/services/question.service';
import notificationService from '@/shared/services/notification.service';
import {
  QUESTION_SECTIONS,
  UNASSIGNED_SECTION,
} from '@/constants/question_sections';
import {
  countActiveFilters,
  fromCriteria,
  toCriteria,
} from '@/shared/utils/list-filters';

/**
 * L'etat de repos de chaque critere. Tout ce qui s'en ecarte filtre, compte
 * dans la pastille du panneau, porte une pastille sous la barre, et part dans
 * le prereglage qu'on enregistre.
 */
const EMPTY_FILTERS = {
  search: '',
  sections: [],
  types: [],
  status: '',
  required: '',
  hasOptions: '',
};

export default {
  name: 'QuestionsListView',
  components: {
    ListHeader,
    FilterChips,
    QuestionFilters,
    QuestionRow,
    QuestionPanel,
    NativeFieldsCard,
  },
  data() {
    return {
      questions: [],
      loading: false,
      error: false,
      errorMessage: '',
      typesLookup: {},
      typesList: [],
      filters: { ...EMPTY_FILTERS },
      filtersOpen: false,
      appliedFilterId: null,
      /*
        Index de section -> depliee. Les sections partaient ouvertes: sur un
        ecran qui en empile six, plus la carte des champs natifs, la page
        s'ouvrait sur un mur qu'on parcourt a la molette. On ouvre ce qu'on
        vient regler, et on garde la vue d'ensemble.
      */
      expanded: {},
      panelOpen: false,
      panelQuestion: null,
      panelSection: -1,
      saving: false,
    };
  },
  computed: {
    filtered() {
      const f = this.filters;
      const needle = f.search.trim().toLowerCase();

      return this.questions.filter((q) => {
        // La recherche porte aussi sur le texte d'aide: c'est souvent la
        // qu'est ecrit ce dont on se souvient.
        if (
          needle &&
          !`${q.label || ''} ${q.helpText || ''}`.toLowerCase().includes(needle)
        ) {
          return false;
        }
        if (f.sections.length && !f.sections.includes(q.step)) {
          return false;
        }
        if (f.types.length && !f.types.includes(String(q.type).toUpperCase())) {
          return false;
        }
        if (f.status === 'visible' && !q.isVisible) {
          return false;
        }
        if (f.status === 'hidden' && q.isVisible) {
          return false;
        }
        if (f.required === 'yes' && !q.isRequired) {
          return false;
        }
        if (f.required === 'no' && q.isRequired) {
          return false;
        }

        const hasOptions = (q.options?.length ?? 0) > 0;

        if (f.hasOptions === 'yes' && !hasOptions) {
          return false;
        }
        if (f.hasOptions === 'no' && hasOptions) {
          return false;
        }

        return true;
      });
    },

    activeCount() {
      return countActiveFilters(this.filters, EMPTY_FILTERS);
    },

    /** Ce qu'on enregistre sous un nom: les criteres qui filtrent, rien d'autre. */
    criteria() {
      return toCriteria(this.filters, EMPTY_FILTERS);
    },

    /**
     * One entry per wizard step, plus a trailing bucket for questions with no
     * section. Sections with no matching question are dropped while filtering,
     * so the accordion never shows a run of empty frames.
     */
    sections() {
      const buckets = QUESTION_SECTIONS.map((section, index) => ({
        index,
        num: index + 1,
        name: section.name,
        rows: [],
      }));
      const unassigned = {
        index: -1,
        num: buckets.length + 1,
        name: UNASSIGNED_SECTION,
        rows: [],
      };

      this.filtered.forEach((q) => {
        const bucket = buckets[q.step];
        (bucket || unassigned).rows.push(q);
      });

      const all = unassigned.rows.length ? [...buckets, unassigned] : buckets;

      return this.hasFilters ? all.filter((s) => s.rows.length) : all;
    },

    chips() {
      const out = [];
      const f = this.filters;

      if (f.search) {
        out.push({ key: 'search', label: `Recherche : ${f.search}` });
      }
      if (f.sections.length) {
        const labels = f.sections.map(
          (index) => QUESTION_SECTIONS[index]?.name ?? UNASSIGNED_SECTION,
        );
        out.push({ key: 'sections', label: `Sections : ${labels.join(', ')}` });
      }
      if (f.types.length) {
        const labels = f.types.map((type) =>
          questionTypeLabel(type, this.typesLookup),
        );
        out.push({ key: 'types', label: `Types : ${labels.join(', ')}` });
      }
      if (f.status) {
        out.push({
          key: 'status',
          label: `Statut : ${f.status === 'visible' ? 'Visible' : 'Masquée'}`,
        });
      }
      if (f.required) {
        out.push({
          key: 'required',
          label: f.required === 'yes' ? 'Obligatoires' : 'Facultatives',
        });
      }
      if (f.hasOptions) {
        out.push({
          key: 'hasOptions',
          label: f.hasOptions === 'yes' ? 'Avec options' : 'Sans options',
        });
      }

      return out;
    },

    hasFilters() {
      return this.chips.length > 0;
    },

    noResults() {
      return !this.loading && this.filtered.length === 0;
    },
  },
  watch: {
    /** Un nouveau filtre rend la main a l'ouverture automatique. */
    filters: {
      deep: true,
      handler() {
        this.expanded = {};
      },
    },
  },
  async created() {
    await this.fetchQuestionTypes();
    this.fetchQuestions();
  },
  methods: {
    normalizeQuestion(q = {}) {
      return {
        id: q.id,
        label: q.label,
        type: q.type ? String(q.type).toUpperCase() : null,
        isVisible: !!q.isVisible,
        step: typeof q.step === 'number' ? q.step : -1,
        displayOrder: q.displayOrder,
        isRequired: !!q.isRequired,
        helpText: q.helpText || null,
        options: Array.isArray(q.options)
          ? q.options.map((opt) => ({
              id: opt.id,
              label: opt.label,
              value: opt.value,
              order: opt.order,
              isOther: !!opt.isOther,
            }))
          : [],
      };
    },

    async fetchQuestionTypes() {
      try {
        const lookup = await questionService.getQuestionTypesLookup();
        this.typesLookup = lookup || {};
        const rawTypes = Object.values(lookup).map((v) => v.key);
        this.typesList = rawTypes.length ? rawTypes : [];
      } catch (err) {
        console.error('Failed to load question types metadata', err);
        this.typesLookup = {};
        this.typesList = [];
      }
    },

    async fetchQuestions() {
      this.loading = true;
      this.error = false;
      this.errorMessage = '';

      try {
        const res = await questionService.getQuestions({ visible: false });
        const payload =
          res?.data?.data && Array.isArray(res.data.data)
            ? res.data.data
            : res?.data && Array.isArray(res.data)
              ? res.data
              : [];
        this.questions = payload.map(this.normalizeQuestion);
      } catch (err) {
        console.error('Failed to fetch questions', err);
        this.error = true;
        this.errorMessage =
          err.response?.data?.message || err.message || 'Erreur inconnue';
      } finally {
        this.loading = false;
      }
    },

    toggleSection(index) {
      this.expanded = { ...this.expanded, [index]: !this.isOpen(index) };
    },

    /*
      Une recherche deplie ce qu'elle a retenu: filtrer puis ne voir que des
      en-tetes fermes donnerait a croire que le filtre n'a rien trouve.
    */
    isOpen(index) {
      if (this.expanded[index] !== undefined) {
        return this.expanded[index];
      }

      return this.hasFilters;
    },

    countLabel(section) {
      const n = section.rows.length;
      return `${n} question${n > 1 ? 's' : ''}`;
    },

    openCreate(sectionIndex = -1) {
      this.panelQuestion = null;
      this.panelSection = sectionIndex ?? -1;
      this.panelOpen = true;
    },

    editQuestion(question) {
      this.panelQuestion = question;
      this.panelSection = question.step ?? -1;
      this.panelOpen = true;
    },

    closePanel() {
      this.panelOpen = false;
      this.panelQuestion = null;
    },

    // The panel is the single entry point; it decides create vs update by id.
    async savePanel(payload) {
      this.saving = true;

      try {
        if (payload.id) {
          await this.updateQuestion(payload);
        } else {
          await this.createQuestion(payload);
        }
      } finally {
        this.saving = false;
      }
    },

    clearFilter(key) {
      this.filters = { ...this.filters, [key]: EMPTY_FILTERS[key] };
      this.appliedFilterId = null;
    },

    clearAllFilters() {
      this.filters = { ...EMPTY_FILTERS };
      this.appliedFilterId = null;
    },

    /**
     * Rappelle un prereglage: ses criteres sont reposes, et le tri se refait
     * aussitot - tout le catalogue est deja charge, rien a redemander.
     */
    applySavedFilter(filter) {
      if (!filter) {
        return;
      }

      this.filters = fromCriteria(filter.criteria, EMPTY_FILTERS);
      this.appliedFilterId = filter.id;
      this.filtersOpen = countActiveFilters(this.filters, EMPTY_FILTERS) > 0;
    },

    /**
     * Le prereglage marque par defaut s'applique a l'ouverture, mais jamais
     * par-dessus des criteres deja regles a la main.
     */
    onSavedFiltersLoaded(defaultFilter) {
      if (defaultFilter && this.activeCount === 0) {
        this.applySavedFilter(defaultFilter);
      }
    },

    toggleRequired(question) {
      this.updateQuestion({ ...question, isRequired: !question.isRequired });
    },

    toggleVisible(question) {
      this.updateQuestion({ ...question, isVisible: !question.isVisible });
    },

    async createQuestion(payload) {
      if (!payload || !payload.label || !payload.type) {
        return notificationService.toastError(
          'Le libellé et le type sont requis',
        );
      }

      payload.type = String(payload.type).toUpperCase();

      try {
        const res = await questionService.createQuestion(payload);
        const created = res?.data?.data || res?.data || null;

        this.questions.push(
          this.normalizeQuestion(
            created || { ...payload, id: 'q_' + Date.now() },
          ),
        );
        this.closePanel();
      } catch (err) {
        console.error('Failed to create question', err);
        notificationService.errorAction(
          'create',
          'question',
          err.response?.data?.message || err.message || 'Échec de la création',
        );
      }
    },

    async updateQuestion(updated) {
      if (!updated || !updated.id) {
        return this.createQuestion(updated);
      }

      if (updated.type) {
        updated.type = String(updated.type).toUpperCase();
      }

      const idx = this.questions.findIndex((q) => q.id === updated.id);
      const previous = idx !== -1 ? { ...this.questions[idx] } : null;

      if (idx !== -1) {
        this.questions.splice(idx, 1, { ...this.questions[idx], ...updated });
      }

      try {
        const res = await questionService.updateQuestion(updated.id, updated);
        const server = res?.data?.data || res?.data || null;

        if (server) {
          const normalized = this.normalizeQuestion(server);
          const i = this.questions.findIndex((q) => q.id === normalized.id);
          if (i !== -1) {
            this.questions.splice(i, 1, normalized);
          }
        }

        if (this.panelQuestion?.id === updated.id) {
          this.closePanel();
        }
      } catch (err) {
        console.error('Failed to update question', err);
        notificationService.errorAction(
          'update',
          'question',
          err.response?.data?.message || err.message || 'Erreur inconnue',
        );

        if (previous && idx !== -1) {
          this.questions.splice(idx, 1, previous);
        }
      }
    },

    async deleteQuestion(q) {
      if (!q || !q.id) {
        return;
      }

      const confirmRes = await notificationService.confirm(
        'Confirmer la suppression',
        'Voulez-vous vraiment supprimer cette question ?',
      );

      if (!confirmRes || !confirmRes.isConfirmed) {
        return;
      }

      const previously = this.questions.slice();
      this.questions = this.questions.filter((x) => x.id !== q.id);

      try {
        await questionService.deleteQuestion(q.id);
      } catch (err) {
        console.error('Failed to delete question', err);
        notificationService.errorAction(
          'delete',
          'question',
          err.response?.data?.message || err.message || 'Erreur inconnue',
        );
        this.questions = previously;
      }
    },
  },
};
</script>

<template>
  <div class="questions-list-container mt-4">
    <!-- Les champs natifs en premier: c'est le questionnaire lui-meme, et ce
         qu'on y regle vaut pour toutes les enquetes. Les questions ajoutees
         viennent apres, dans leur propre carte. -->
    <NativeFieldsCard />

    <div class="list-card">
      <ListHeader title="Questions personnalisées">
        <template #actions>
          <button
            type="button"
            class="list-btn list-btn--primary"
            @click="openCreate(-1)"
          >
            <i class="ri-add-line"></i>
            Ajouter une question
          </button>
        </template>
      </ListHeader>

      <p class="questions-note">
        Questions personnalisées uniquement. Les champs standards de l'enquête
        ne sont pas listés ici.
      </p>

      <QuestionFilters
        v-model="filters"
        v-model:expanded="filtersOpen"
        :types-list="typesList"
        :types-lookup="typesLookup"
        :active-count="activeCount"
        :criteria="criteria"
        :applied-filter-id="appliedFilterId"
        @reset="clearAllFilters"
        @apply-saved="applySavedFilter"
        @saved-filters-loaded="onSavedFiltersLoaded"
      />

      <FilterChips
        :chips="chips"
        @clear="clearFilter"
        @clear-all="clearAllFilters"
      />

      <p v-if="error" class="list-card__error" role="alert">
        {{ errorMessage }}
      </p>

      <p v-if="loading" class="list-card__state">Chargement des questions…</p>

      <div v-else-if="noResults" class="list-card__empty">
        <p class="list-card__empty-text">
          {{
            hasFilters
              ? 'Aucune question ne correspond à ces filtres.'
              : 'Aucune question personnalisée pour le moment.'
          }}
        </p>
        <button
          v-if="hasFilters"
          type="button"
          class="list-card__empty-action"
          @click="clearAllFilters"
        >
          Effacer les filtres
        </button>
      </div>

      <div v-else class="questions-sections">
        <div v-for="section in sections" :key="section.index">
          <div
            class="questions-section__head"
            role="button"
            tabindex="0"
            @click="toggleSection(section.index)"
            @keyup.enter="toggleSection(section.index)"
          >
            <i
              class="questions-section__chevron"
              :class="
                isOpen(section.index)
                  ? 'ri-arrow-down-s-line'
                  : 'ri-arrow-right-s-line'
              "
            ></i>
            <span class="questions-section__num">{{ section.num }}</span>
            <span class="questions-section__name">{{ section.name }}</span>
            <span class="questions-section__count">
              · {{ countLabel(section) }}
            </span>
            <button
              type="button"
              class="questions-section__add"
              @click.stop="openCreate(section.index)"
            >
              Ajouter
            </button>
          </div>

          <div v-if="isOpen(section.index)">
            <QuestionRow
              v-for="q in section.rows"
              :key="q.id"
              :question="q"
              :types-lookup="typesLookup"
              @edit="editQuestion"
              @delete="deleteQuestion"
              @toggle-required="toggleRequired"
              @toggle-visible="toggleVisible"
            />

            <div v-if="!section.rows.length" class="questions-section__empty">
              <span>Aucune question dans cette section.</span>
              <button
                type="button"
                class="list-card__empty-action"
                @click="openCreate(section.index)"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <QuestionPanel
      v-if="panelOpen"
      :key="panelQuestion ? panelQuestion.id : 'new'"
      :question="panelQuestion"
      :default-section="panelSection"
      :types-list="typesList"
      :types-lookup="typesLookup"
      :saving="saving"
      @save="savePanel"
      @close="closePanel"
    />
  </div>
</template>
